"use client"

import React, { useState } from "react"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { FileIcon, CalendarIcon, TrashIcon } from "lucide-react"
import { Button } from "@/components/ui/button"
import { InvoiceCardProps } from "./invoice-card.types"
import { Logo } from "@/components/ui";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu"
import dayjs from "dayjs"
import './style.scss';
import { InvoiceExtended } from "@/types"
import { EllipsisVertical } from "lucide-react";

function InvoiceCard({invoice, selectedInvoice, onSelect, onRemove}: InvoiceCardProps) {

  const { id, invoiceId, seller, currency, totalAmount, issueDate, paymentDueDate } = invoice as InvoiceExtended;
  
  const [tags] = useState<string[]>(() => {
    if (!seller) return [];
    const mainActivities = seller.mainActivities.flatMap(activity => [activity.purpose, activity.activity, activity.secondaryPurpose]);
    const secondaryActivities = seller.secondaryActivities.map(activity => activity.purpose);
    return [...mainActivities, ...secondaryActivities].filter(Boolean) as string[];
  });

  const [_cardState, setCardState] = useState("")

  const currencyFormatter = currency ? new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency == 'Ft' ? 'HUF' : currency,
    }) : { format: (value: number) => value.toString() }

  const selectHandler = () => {
    if(onSelect && invoice) onSelect(invoice)
    console.log('Card selected', invoice)   
    setCardState('SELECTED');
  }

  return (
    <Card key={`invoice-${invoice?.id}`} className={`w-full ${selectedInvoice && selectedInvoice.id === invoice?.id ? 'selected-card' : ''} `} onClick={selectHandler}>
      <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <FileIcon className="h-5 w-5" aria-hidden="true" />
            <span>{invoiceId}</span>
            
            <div className="ml-auto">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon">
                    <EllipsisVertical className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent>
                  <DropdownMenuItem onClick={() => onRemove && onRemove(id)}>
                    <TrashIcon className="h-4 w-4 mr-2" />
                    Remove Invoice
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
            
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-4 mb-4">
            <div className="col-span-1">
              <h3 className="text-sm font-medium text-gray-500">ISSUED BY</h3>
              {seller && <span className={`flex items-center space-x-2`}>
                  <Logo url={seller.corporateWebsiteURL} shortName={seller.shortCompanyName} longName={seller.companyName} />
                  <p className="text-lg font-semibold">{seller?.shortCompanyName || seller?.companyName || 'N/A'}</p>
                </span>
              }
            </div>
            <div className="col-span-1 flex justify-between">
              {totalAmount && <div>
                <h3 className="text-sm font-medium text-gray-500">AMOUNT</h3>
                <p className="text-lg font-semibold">
                  {currencyFormatter.format(totalAmount)}
                </p>
              </div>}
              {issueDate && <div>
                <h3 className="text-sm font-medium text-gray-500">ISSUE DATE</h3>
                <p className="text-lg font-semibold flex items-center">
                  <CalendarIcon className="h-4 w-4 mr-1" aria-hidden="true" />
                  <span>{dayjs(issueDate).format('MMM DD')}</span>
                </p>
              </div>}
              {paymentDueDate && <div>
                <h3 className="text-sm font-medium text-gray-500">DUE DATE</h3>
                <p className="text-lg font-semibold flex items-center">
                  <CalendarIcon className="h-4 w-4 mr-1" aria-hidden="true" />
                  <span>{dayjs(paymentDueDate).format('MMM DD')}</span>
                </p>
              </div>}
            </div>
          </div>
          <div>
            <div className="flex flex-wrap gap-2 mb-2">
              {tags && tags.length > 0 && tags.map((tag, index) => (
                <Badge key={invoice?.id + tag + invoiceId + index } variant="secondary">{tag}</Badge>
              ))}
            </div>
          </div>
        </CardContent>
    </Card>
  )
}
export { InvoiceCard }